import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Typography,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
  Divider,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Person as PersonIcon,
  Business as BusinessIcon,
  Assignment as AssignmentIcon,
  VideoCall as VideoCallIcon,
  Link as LinkIcon,
  Event as EventIcon,
  CheckCircle as CheckCircleIcon,
  History as HistoryIcon,
  AttachMoney as AttachMoneyIcon,
  Groups as GroupsIcon,
  Description as DescriptionIcon,
  EventAvailable as EventAvailableIcon,
  Receipt as ReceiptIcon,
  FolderOpen as FolderOpenIcon,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import api, { downloadAttachment } from '../services/api';
import caseService from '../services/case.service';
import { useAuth } from '../hooks/useAuth';
import RetainerForm from '../components/RetainerForm';
import CaseCrmPanel from '../components/CaseCrmPanel';
import { getInterpreterLanguageLabel } from '../data/interpreterLanguages';
import { intakeFormService } from '../services/intakeForm.service';

interface CaseNote {
  id: number;
  note: string;
  action_type: string;
  metadata?: any;
  created_at: string;
  user: {
    id: number;
    name: string;
  };
}

interface Case {
  id: number;
  case_name: string;
  client_email?: string;
  case_type: string;
  case_state?: string;
  service_type_id: number;
  service_type?: { name: string; rush_fee_amount?: number; rush_tiers?: { key: string; label: string; min_days: number; max_days: number; fee: number }[] };
  service_type_price?: number;
  rush_fee_amount?: number;
  rush_tier?: string | null;
  total_amount?: number;
  retainer_amount?: number;
  balance_amount?: number;
  due_date: string;
  is_rush?: boolean;
  interpreter_languages?: string[];
  interpreter_other_language?: string;
  status:
    | 'Created'
    | 'Prospecting'
    | 'Approved'
    | 'Retainer_Received'
    | 'In_Person_Requested'
    | 'Virtual_Meeting_Requested'
    | 'Meeting_Scheduled'
    | 'Evaluation_Completed'
    | 'Rejected'
    | 'Meeting_Booked';
  rejection_comment?: string;
  firm?: { name: string };
  creator?: { name: string };
  notes?: CaseNote[];
  attachments?: CaseAttachment[];
  meeting_link?: string;
  meeting_status?: string;
  meeting_slot_id?: number;
  bookedSlot?: {
    id: number;
    available_date: string;
    time_slot: string;
    booked_at: string;
    booked_by: {
      name: string;
      email: string;
    };
  };
  // Retainer and invoice fields
  retainer_form_file_path?: string;
  retainer_experts?: number[];
  invoice_file_path?: string;
  invoice_status?: 'paid' | 'unpaid';
  invoice_paid_at?: string;
  invoice_paid_by?: {
    id: number;
    name: string;
  };
  invoice_date?: string;
  payment_date?: string;
  payment_status?: string;
  assigned_expert?: {
    id: number;
    name: string;
    email: string;
  };
  firm_id?: number;
  opportunity_id?: string | null;
  file_number?: string | null;
  claim_number?: string | null;
  opportunity_follow_up?: string | null;
  sales_stage_id?: number | null;
  sales_stage?: { id: number; name: string } | null;
  attorney_id?: number | null;
  paralegal_id?: number | null;
  attorney?: { id: number; name: string; email?: string; phone?: string | null; phone_extension?: string | null } | null;
  paralegal?: { id: number; name: string; email?: string; phone?: string | null; phone_extension?: string | null } | null;
}

interface CaseAttachment {
  id: number;
  name: string;
  file_path: string;
  external_url?: string | null;
  type: 'service_type' | 'expert_resume' | 'medical_records';
  created_at: string;
}

const ATTACHMENT_TYPE_LABEL: Record<CaseAttachment['type'], string> = {
  service_type: 'Service document',
  expert_resume: 'Expert resume',
  medical_records: 'Medical record',
};

function hasRole(user: any, roleName: string) {
  return user?.roles?.some((r: any) => r.name === roleName);
}

const sectionCardSx = {
  borderRadius: 2,
  boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
  border: '1px solid',
  borderColor: 'divider',
};

const DetailField: React.FC<{
  label: string;
  children: React.ReactNode;
}> = ({ label, children }) => (
  <Box>
    <Typography
      variant='caption'
      color='text.secondary'
      sx={{ fontWeight: 600, letterSpacing: 0.4, textTransform: 'uppercase' }}
    >
      {label}
    </Typography>
    <Box sx={{ mt: 0.5 }}>{children}</Box>
  </Box>
);

const SectionCard: React.FC<{
  title: string;
  subtitle?: string;
  icon?: React.ReactNode;
  action?: React.ReactNode;
  children: React.ReactNode;
  sx?: object;
}> = ({ title, subtitle, icon, action, children, sx }) => (
  <Card sx={{ ...sectionCardSx, overflow: 'hidden', ...sx }}>
    <Box
      sx={{
        px: 2.5,
        py: 1.75,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 2,
        borderBottom: '1px solid',
        borderColor: 'divider',
        bgcolor: theme => theme.palette.mode === 'light' ? 'grey.50' : 'action.hover',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}>
        {icon && (
          <Avatar
            variant='rounded'
            sx={{
              width: 38,
              height: 38,
              bgcolor: 'primary.main',
              color: 'primary.contrastText',
            }}
          >
            {icon}
          </Avatar>
        )}
        <Box>
          <Typography variant='subtitle1' fontWeight={700} lineHeight={1.3}>
            {title}
          </Typography>
          {subtitle && (
            <Typography variant='caption' color='text.secondary' display='block'>
              {subtitle}
            </Typography>
          )}
        </Box>
      </Box>
      {action}
    </Box>
    <CardContent sx={{ pt: 2.5, '&:last-child': { pb: 2.5 } }}>{children}</CardContent>
  </Card>
);

const CaseSummary: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [caseData, setCaseData] = useState<Case | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showAddNote, setShowAddNote] = useState(false);
  const [showStatusDialog, setShowStatusDialog] = useState(false);
  const [newNote, setNewNote] = useState('');
  const [noteType, setNoteType] = useState('comment');
  const [newStatus, setNewStatus] = useState('');
  const [statusNote, setStatusNote] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [downloadingAttachments, setDownloadingAttachments] = useState<{
    [key: string]: boolean;
  }>({});
  const [showTeamsMeetingDialog, setShowTeamsMeetingDialog] = useState(false);
  const [teamsMeetingSubject, setTeamsMeetingSubject] = useState('');
  const [teamsMeetingBody, setTeamsMeetingBody] = useState('');
  const [creatingTeamsMeeting, setCreatingTeamsMeeting] = useState(false);
  const [showRetainerFormDialog, setShowRetainerFormDialog] = useState(false);
  const [markingInvoicePaid, setMarkingInvoicePaid] = useState(false);
  const [downloadingRetainerForm, setDownloadingRetainerForm] = useState(false);
  const [downloadingInvoice, setDownloadingInvoice] = useState(false);
  const [intakeFormLinks, setIntakeFormLinks] = useState<
    Array<{
      intake_form_type: any;
      intake_form: any;
      public_url: string;
      is_required: boolean;
      sort_order: number;
      status: string;
    }>
  >([]);
  const [loadingIntakeFormLinks, setLoadingIntakeFormLinks] = useState(false);
  const [downloadingIntakeFormPdf, setDownloadingIntakeFormPdf] =
    useState(false);
  // const [generatingIntakeFormPdf, setGeneratingIntakeFormPdf] = useState(false);

  // Financial dialog states
  const [showRetainerDialog, setShowRetainerDialog] = useState(false);
  const [showInvoiceDialog, setShowInvoiceDialog] = useState(false);
  const [retainerAmount, setRetainerAmount] = useState('');
  const [invoiceDate, setInvoiceDate] = useState('');
  const [paymentDate, setPaymentDate] = useState('');
  const [updatingRetainer, setUpdatingRetainer] = useState(false);
  const [updatingInvoice, setUpdatingInvoice] = useState(false);

  // Rush tier dialog (admin only)
  const [showRushTierDialog, setShowRushTierDialog] = useState(false);
  const [selectedRushTier, setSelectedRushTier] = useState<string>('');
  const [applyingRushTier, setApplyingRushTier] = useState(false);

  const isAdmin = hasRole(user, 'admin');
  const isFirmAdmin = hasRole(user, 'firm_admin');
  const isExpertUser = hasRole(user, 'expert_user');

  const fetchCaseDetails = async () => {
    if (!id) return;

    setLoading(true);
    setError('');
    try {
      const res = await caseService.getCase(Number(id));
      // filter out notes data here
      if (isFirmAdmin && !isAdmin && !isExpertUser) {
        res.data.notes = res.data.notes.filter(
          (note: { action_type: string }) => note.action_type === 'comment'
        );
      }
      setCaseData(res.data);

      // Fetch intake form links
      await fetchIntakeFormLinks();
    } catch (err: any) {
      if (err.response?.status === 403) {
        setError('You do not have permission to view this case');
      } else if (err.response?.status === 404) {
        setError('Case not found');
      } else {
        setError('Failed to load case details');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!authLoading && (isAdmin || isFirmAdmin || isExpertUser) && id) {
      fetchCaseDetails();
    }
  }, [authLoading, isAdmin, isFirmAdmin, isExpertUser, id]);

  const handleAddNote = async () => {
    if (!newNote.trim() || !id) return;

    setSubmitting(true);
    try {
      const res = await caseService.addNote(Number(id), {
        note: newNote,
        action_type: noteType,
      });

      // Add the new note to the case data
      setCaseData(prev =>
        prev
          ? {
              ...prev,
              notes: [res.data, ...(prev.notes || [])],
            }
          : null
      );

      setNewNote('');
      setNoteType('comment');
      setShowAddNote(false);
      toast.success('Note added successfully!');
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Failed to add note');
    } finally {
      setSubmitting(false);
    }
  };

  const handleUpdateStatus = async () => {
    if (!newStatus || !id) return;

    setSubmitting(true);
    try {
      await caseService.updateStatus(Number(id), {
        status: newStatus,
        note: statusNote,
      });

      // Update the case status in the local state
      setCaseData(prev =>
        prev
          ? {
              ...prev,
              status: newStatus as any,
              rejection_comment:
                newStatus === 'Rejected' ? statusNote : undefined,
            }
          : null
      );

      setNewStatus('');
      setStatusNote('');
      setShowStatusDialog(false);
      toast.success('Case status updated successfully!');
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Failed to update status');
    } finally {
      setSubmitting(false);
    }
  };

  const handleRetainerReceived = async () => {
    // Instead of marking retainer received immediately, just open the retainer form
    setShowRetainerFormDialog(true);
  };

  const handleRetainerFormSuccess = async () => {
    setShowRetainerFormDialog(false);
    fetchCaseDetails(); // Refresh case data to show new retainer form info and updated status
    toast.success('Retainer form submitted successfully!');
  };

  const handleDownloadRetainerForm = async () => {
    if (!id) return;

    setDownloadingRetainerForm(true);
    try {
      const response = await api.get(`/cases/${id}/download-retainer-form`, {
        responseType: 'blob',
      });

      // Create a blob URL and trigger download
      const blob = new Blob([response.data]);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `retainer_form_${caseData?.case_name || 'case'}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      toast.success('Retainer form downloaded successfully!');
    } catch (error: any) {
      console.error('Failed to download retainer form:', error);
      toast.error(
        error.response?.data?.message || 'Failed to download retainer form'
      );
    } finally {
      setDownloadingRetainerForm(false);
    }
  };

  const handleDownloadInvoice = async () => {
    if (!id) return;

    setDownloadingInvoice(true);
    try {
      const response = await api.get(`/cases/${id}/download-invoice`, {
        responseType: 'blob',
      });

      // Create a blob URL and trigger download
      const blob = new Blob([response.data]);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `invoice_${caseData?.case_name || 'case'}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      toast.success('Invoice downloaded successfully!');
    } catch (error: any) {
      console.error('Failed to download invoice:', error);
      toast.error(
        error.response?.data?.message || 'Failed to download invoice'
      );
    } finally {
      setDownloadingInvoice(false);
    }
  };

  const handleMarkInvoicePaid = async () => {
    if (!id) return;

    setMarkingInvoicePaid(true);
    try {
      await caseService.markInvoicePaid(Number(id));

      // Update the case data in the local state
      setCaseData(prev =>
        prev
          ? {
              ...prev,
              invoice_status: 'paid',
              invoice_paid_at: new Date().toISOString(),
              invoice_paid_by: user
                ? { id: user.id, name: user.name }
                : undefined,
            }
          : null
      );

      toast.success('Invoice marked as paid successfully!');
    } catch (err: any) {
      toast.error(
        err.response?.data?.message || 'Failed to mark invoice as paid'
      );
    } finally {
      setMarkingInvoicePaid(false);
    }
  };

  const handleCreateTeamsMeeting = async () => {
    if (!id) return;

    setCreatingTeamsMeeting(true);
    try {
      const response = await caseService.createTeamsMeeting(Number(id), {
        slot_id: caseData?.meeting_slot_id || 0,
        meeting_subject: teamsMeetingSubject,
        meeting_body: teamsMeetingBody,
      });

      // Update case data with meeting link
      setCaseData(prev =>
        prev
          ? {
              ...prev,
              meeting_link: response.data.meeting_link,
              meeting_status: 'scheduled',
            }
          : null
      );

      setTeamsMeetingSubject('');
      setTeamsMeetingBody('');
      setShowTeamsMeetingDialog(false);
      toast.success('Teams meeting created successfully!');
    } catch (err: any) {
      toast.error(
        err.response?.data?.message || 'Failed to create Teams meeting'
      );
    } finally {
      setCreatingTeamsMeeting(false);
    }
  };

  const fetchIntakeFormLinks = async () => {
    if (!id) return;

    setLoadingIntakeFormLinks(true);
    try {
      const response = await intakeFormService.getIntakeFormLinks(Number(id));
      setIntakeFormLinks(response.intake_form_links);
    } catch (err: any) {
      console.error('Failed to fetch intake form links:', err);
      // Don't show error toast as this is not critical
    } finally {
      setLoadingIntakeFormLinks(false);
    }
  };

  const handleDownloadIntakeFormPdf = async () => {
    if (!id) return;

    setDownloadingIntakeFormPdf(true);
    try {
      const blob = await intakeFormService.downloadIntakeFormPdf(Number(id));

      // Create a blob URL and trigger download
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `intake_form_${caseData?.case_name || 'case'}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      toast.success('Intake form PDF downloaded successfully!');
    } catch (error: any) {
      console.error('Failed to download intake form PDF:', error);

      // Handle specific error cases
      if (error.response?.data?.message === 'Intake form PDF not found') {
        toast.error(
          'PDF not available. The intake form must be completed by the client first, or you can generate a new PDF.'
        );
      } else {
        toast.error(
          error.response?.data?.message || 'Failed to download intake form PDF'
        );
      }
    } finally {
      setDownloadingIntakeFormPdf(false);
    }
  };

  // Handle retainer amount update
  const handleUpdateRetainer = async () => {
    if (!id || !retainerAmount) return;

    setUpdatingRetainer(true);
    try {
      const response = await caseService.updateRetainerAmount(Number(id), {
        retainer_amount: parseFloat(retainerAmount),
      });

      // Update case data with new retainer amount
      setCaseData(prev =>
        prev
          ? {
              ...prev,
              retainer_amount: response.data.retainer_amount,
              balance_amount: response.data.balance_amount,
            }
          : null
      );

      toast.success('Retainer amount updated successfully');
      setShowRetainerDialog(false);
      setRetainerAmount('');
    } catch (error: any) {
      console.error('Error updating retainer amount:', error);
      toast.error(error.response?.data?.message || 'Failed to update retainer amount');
    } finally {
      setUpdatingRetainer(false);
    }
  };

  // Handle invoice details update
  const handleUpdateInvoiceDetails = async () => {
    if (!id) return;

    setUpdatingInvoice(true);
    try {
      const response = await caseService.updateInvoiceDetails(Number(id), {
        invoice_date: invoiceDate || undefined,
        payment_date: paymentDate || undefined,
      });

      // Update case data with new invoice details
      setCaseData(prev =>
        prev
          ? {
              ...prev,
              invoice_date: response.data.invoice_date,
              payment_date: response.data.payment_date,
            }
          : null
      );

      toast.success('Invoice details updated successfully');
      setShowInvoiceDialog(false);
      setInvoiceDate('');
      setPaymentDate('');
    } catch (error: any) {
      console.error('Error updating invoice details:', error);
      toast.error(error.response?.data?.message || 'Failed to update invoice details');
    } finally {
      setUpdatingInvoice(false);
    }
  };

  const handleApplyRushTier = async () => {
    if (!id) return;
    setApplyingRushTier(true);
    try {
      const response = await api.patch(`/cases/${id}/apply-rush-tier`, {
        rush_tier: selectedRushTier || null,
      });
      setCaseData(response.data.data);
      toast.success(response.data.message);
      setShowRushTierDialog(false);
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to apply rush tier');
    } finally {
      setApplyingRushTier(false);
    }
  };

  // const handleGenerateIntakeFormPdf = async () => {
  //   if (!id) return;

  //   setGeneratingIntakeFormPdf(true);
  //   try {
  //     await intakeFormService.generateIntakeFormPdf(Number(id));
  //     toast.success('Intake form PDF generated successfully!');
  //     // Refresh intake form links to get updated status
  //     await fetchIntakeFormLinks();
  //   } catch (error: any) {
  //     console.error('Failed to generate intake form PDF:', error);
  //     toast.error(
  //       error.response?.data?.message || 'Failed to generate intake form PDF'
  //     );
  //   } finally {
  //     setGeneratingIntakeFormPdf(false);
  //   }
  // };

  const getStatusChip = (status: string, _rejectionComment?: string) => {
    switch (status) {
      case 'Created':
        return <Chip label='Created' color='warning' onClick={() => false} />;
      case 'Prospecting':
        return (
          <Box
            component='span'
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              padding: '4px 12px',
              borderRadius: '16px',
              backgroundColor: 'info.main',
              color: 'info.contrastText',
              fontSize: '0.75rem',
              fontWeight: 500,
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
            }}
          >
            Prospecting
          </Box>
        );
      case 'Approved':
        return (
          <Box
            component='span'
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              padding: '4px 12px',
              borderRadius: '16px',
              backgroundColor: 'success.main',
              color: 'success.contrastText',
              fontSize: '0.75rem',
              fontWeight: 500,
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
            }}
          >
            Approved
          </Box>
        );
      case 'Retainer_Received':
        return (
          <Box
            component='span'
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              padding: '4px 12px',
              borderRadius: '16px',
              backgroundColor: 'purple',
              color: 'white',
              fontSize: '0.75rem',
              fontWeight: 500,
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
            }}
          >
            Retainer Received
          </Box>
        );
      case 'In_Person_Requested':
        return (
          <Box
            component='span'
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              padding: '4px 12px',
              borderRadius: '16px',
              backgroundColor: 'orange',
              color: 'white',
              fontSize: '0.75rem',
              fontWeight: 500,
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
            }}
          >
            In-Person Requested
          </Box>
        );
      case 'Virtual_Meeting_Requested':
        return (
          <Box
            component='span'
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              padding: '4px 12px',
              borderRadius: '16px',
              backgroundColor: 'grey',
              color: 'white',
              fontSize: '0.75rem',
              fontWeight: 500,
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
            }}
          >
            Virtual Meeting Requested
          </Box>
        );
      case 'Meeting_Scheduled':
        return (
          <Box
            component='span'
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              padding: '4px 12px',
              borderRadius: '16px',
              backgroundColor: 'brown',
              color: 'white',
              fontSize: '0.75rem',
              fontWeight: 500,
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
            }}
          >
            Meeting Scheduled
          </Box>
        );
      case 'Evaluation_Completed':
        return (
          <Box
            component='span'
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              padding: '4px 12px',
              borderRadius: '16px',
              backgroundColor: 'success.main',
              color: 'success.contrastText',
              fontSize: '0.75rem',
              fontWeight: 500,
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
            }}
          >
            Evaluation Completed
          </Box>
        );
      case 'Rejected':
        return <Chip label='Rejected' color='error' onClick={() => false} />;
      case 'Meeting_Booked':
        return (
          <Box
            component='span'
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              padding: '4px 12px',
              borderRadius: '16px',
              backgroundColor: 'success.main',
              color: 'success.contrastText',
              fontSize: '0.75rem',
              fontWeight: 500,
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
            }}
          >
            Meeting Booked
          </Box>
        );
      default:
        return <Chip label={status} onClick={() => false} />;
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const handleDownloadAttachment = async (attachment: CaseAttachment) => {
    const attachmentKey = `${attachment.type}_${attachment.id}`;
    setDownloadingAttachments(prev => ({
      ...prev,
      [attachmentKey]: true,
    }));

    try {
      if (attachment.type === 'expert_resume') {
        const expertId = String(attachment.id).replace('expert_', '');
        const response = await api.get(
          `/admin/experts/${expertId}/download-resume`,
          { responseType: 'blob' }
        );

        const contentDisposition = response.headers['content-disposition'];
        let filename = `${attachment.name}.pdf`;
        if (contentDisposition) {
          const filenameMatch = contentDisposition.match(/filename="(.+)"/);
          if (filenameMatch) {
            filename = filenameMatch[1];
          }
        }

        const blob = new Blob([response.data], {
          type: response.headers['content-type'],
        });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      } else if (attachment.type === 'medical_records') {
        const response = await api.get(
          `/medical-records/${id}/download/${attachment.id}`,
          { responseType: 'blob' }
        );

        const contentDisposition = response.headers['content-disposition'];
        let filename = attachment.name;
        if (contentDisposition) {
          const filenameMatch = contentDisposition.match(/filename="(.+)"/);
          if (filenameMatch) {
            filename = filenameMatch[1];
          }
        }

        const blob = new Blob([response.data], {
          type: response.headers['content-type'],
        });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      } else {
        await downloadAttachment(attachment.id);
      }
      toast.success('Download started successfully!');
    } catch (error: any) {
      console.error('Download error:', error);
      toast.error(
        error.response?.data?.message || 'Failed to download file'
      );
    } finally {
      setDownloadingAttachments(prev => ({
        ...prev,
        [attachmentKey]: false,
      }));
    }
  };

  // Show loading while checking authentication
  if (authLoading) {
    return (
      <Box
        display='flex'
        justifyContent='center'
        alignItems='center'
        minHeight='200px'
      >
        <CircularProgress />
      </Box>
    );
  }

  // Show error if user doesn't have permission
  if (!isAdmin && !isFirmAdmin && !isExpertUser) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant='h4' gutterBottom>
          Case Summary
        </Typography>
        <Typography color='error'>
          You do not have permission to view cases.
        </Typography>
      </Box>
    );
  }

  if (loading) {
    return (
      <Box
        display='flex'
        justifyContent='center'
        alignItems='center'
        minHeight='200px'
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/admin/cases')}
          sx={{ mb: 2 }}
        >
          Back to Cases
        </Button>
        <Typography color='error'>{error}</Typography>
      </Box>
    );
  }

  if (!caseData) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography>Case not found</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1400, mx: 'auto' }}>
      {/* Page header */}
      <Paper
        elevation={0}
        sx={{
          mb: 3,
          p: { xs: 2, md: 3 },
          borderRadius: 2,
          border: '1px solid',
          borderColor: 'divider',
          background: theme =>
            `linear-gradient(135deg, ${theme.palette.primary.main}08 0%, ${theme.palette.background.paper} 60%)`,
        }}
      >
        <Stack direction='row' alignItems='center' spacing={1} sx={{ mb: 2 }}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/admin/cases')}
            size='small'
          >
            Back to Cases
          </Button>
        </Stack>

        <Stack
          direction={{ xs: 'column', md: 'row' }}
          alignItems={{ xs: 'flex-start', md: 'center' }}
          justifyContent='space-between'
          spacing={2}
          sx={{ mb: 2 }}
        >
          <Box>
            <Typography variant='h4' sx={{ fontWeight: 700, mb: 1 }}>
              {caseData.case_name}
            </Typography>
            <Stack direction='row' spacing={1} flexWrap='wrap' useFlexGap>
              {getStatusChip(caseData.status, caseData.rejection_comment)}
              {caseData.is_rush && (
                <Chip label='RUSH' color='error' size='small' sx={{ fontWeight: 700 }} />
              )}
              <Chip
                label={caseData.case_type}
                size='small'
                variant='outlined'
                color='primary'
              />
              {caseData.case_state && (
                <Chip label={caseData.case_state} size='small' variant='outlined' />
              )}
              {(caseData.interpreter_languages || []).map((language) => (
                <Chip
                  key={language}
                  label={`Interpreter: ${
                    language === 'other' && caseData.interpreter_other_language
                      ? caseData.interpreter_other_language
                      : getInterpreterLanguageLabel(language)
                  }`}
                  size='small'
                  color='secondary'
                  variant='outlined'
                />
              ))}
            </Stack>
          </Box>
          <Typography variant='body2' color='text.secondary'>
            Case #{caseData.id}
            {caseData.firm?.name ? ` · ${caseData.firm.name}` : ''}
            {caseData.service_type?.name ? ` · ${caseData.service_type.name}` : ''}
          </Typography>
        </Stack>

        <Stack direction='row' spacing={1} flexWrap='wrap' useFlexGap>
          {(isAdmin || isFirmAdmin || isExpertUser) && (
            <Button
              startIcon={<AddIcon />}
              onClick={() => setShowAddNote(true)}
              variant='outlined'
              size='small'
            >
              Add Note
            </Button>
          )}
          {isAdmin && caseData.status === 'Approved' && (
            <Button
              variant='contained'
              color='primary'
              onClick={handleRetainerReceived}
              disabled={submitting}
              size='small'
            >
              Submit Retainer Form
            </Button>
          )}
          {isAdmin && (
            <Button
              variant='outlined'
              startIcon={<EditIcon />}
              onClick={() => setShowStatusDialog(true)}
              size='small'
            >
              Update Status
            </Button>
          )}
          {(isAdmin ||
            (isExpertUser &&
              caseData?.assigned_expert?.id === user?.id)) &&
            !caseData.bookedSlot && (
            <Button
              variant='outlined'
              startIcon={<EventIcon />}
              onClick={() => navigate(`/admin/cases/${id}/slots`)}
              size='small'
            >
              Manage Slots
            </Button>
          )}
          {isAdmin && intakeFormLinks.length > 0 && (
            <Button
              variant='outlined'
              startIcon={<AssignmentIcon />}
              onClick={() => {
                if (intakeFormLinks.length > 0) {
                  window.open(intakeFormLinks[0].public_url, '_blank');
                }
              }}
              size='small'
            >
              View Intake Form
            </Button>
          )}
        </Stack>
      </Paper>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', lg: 'minmax(0, 1fr) 380px' },
          gap: 3,
          alignItems: 'start',
        }}
      >
        {/* Main content column */}
        <Box>
          <CaseCrmPanel caseData={caseData} onSaved={fetchCaseDetails} />

          <SectionCard
            title='Service Information'
            subtitle='Service type and scheduling'
            icon={<DescriptionIcon fontSize='small' />}
            sx={{ mt: 3 }}
          >
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                gap: 2.5,
              }}
            >
              <DetailField label='Service Type'>
                <Stack direction='row' alignItems='center' spacing={1} flexWrap='wrap'>
                  <Typography variant='body1' fontWeight={500}>
                    {caseData.service_type?.name || '-'}
                  </Typography>
                  {caseData.is_rush && (
                    <Chip label='RUSH' color='error' size='small' sx={{ fontWeight: 'bold' }} />
                  )}
                </Stack>
                {caseData.is_rush && caseData.rush_fee_amount != null && (
                  <Typography variant='body2' color='error.main' sx={{ mt: 0.5 }}>
                    Rush Fee: ${Number(caseData.rush_fee_amount).toFixed(2)}
                  </Typography>
                )}
              </DetailField>

              {caseData.due_date && (
                <DetailField label='Due Date'>
                  <Typography variant='body1'>
                    {new Date(caseData.due_date).toLocaleDateString()}
                  </Typography>
                </DetailField>
              )}

              <DetailField label='Created By'>
                <Typography variant='body1'>{caseData.creator?.name || '-'}</Typography>
              </DetailField>
            </Box>

            {caseData.rejection_comment && (
              <Alert severity='error' sx={{ mt: 2.5 }}>
                <Typography variant='subtitle2' sx={{ mb: 0.5 }}>
                  Rejection Comment
                </Typography>
                {caseData.rejection_comment}
              </Alert>
            )}
          </SectionCard>

          {/* Financial Information */}
          {(isAdmin || isFirmAdmin) && (
            <SectionCard
              title='Financial Information'
              subtitle='Pricing, retainer, and invoice status'
              icon={<AttachMoneyIcon fontSize='small' />}
              sx={{ mt: 3 }}
              action={
                isAdmin ? (
                  <Stack direction='row' spacing={1}>
                    <Button
                      variant='outlined'
                      size='small'
                      color='error'
                      onClick={() => {
                        setSelectedRushTier(caseData?.rush_tier || '');
                        setShowRushTierDialog(true);
                      }}
                    >
                      Rush Tier
                    </Button>
                    <Button variant='outlined' size='small' onClick={() => setShowRetainerDialog(true)}>
                      Retainer
                    </Button>
                    <Button variant='outlined' size='small' onClick={() => setShowInvoiceDialog(true)}>
                      Invoice
                    </Button>
                  </Stack>
                ) : undefined
              }
            >
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
                  gap: 2,
                }}
              >
                <DetailField label='Service Price'>
                  <Typography variant='h6' fontWeight={700}>
                    ${Number(caseData.service_type_price || 0).toFixed(2)}
                  </Typography>
                </DetailField>

                {caseData.is_rush && (
                  <DetailField label='Rush Fee'>
                    <Typography variant='h6' fontWeight={700} color='error.main'>
                      ${Number(caseData.rush_fee_amount || 0).toFixed(2)}
                    </Typography>
                    {caseData.rush_tier && caseData.service_type?.rush_tiers && (
                      <Typography variant='caption' color='text.secondary'>
                        {caseData.service_type.rush_tiers.find(t => t.key === caseData.rush_tier)?.label ?? caseData.rush_tier}
                        {' · '}
                        {caseData.service_type.rush_tiers.find(t => t.key === caseData.rush_tier)?.min_days}–
                        {caseData.service_type.rush_tiers.find(t => t.key === caseData.rush_tier)?.max_days} business days
                      </Typography>
                    )}
                  </DetailField>
                )}

                {(caseData.is_rush ||
                  Number(caseData.total_amount) !== Number(caseData.service_type_price)) && (
                  <DetailField label='Total'>
                    <Typography variant='h6' fontWeight={700} color='primary.main'>
                      ${Number(caseData.total_amount || 0).toFixed(2)}
                    </Typography>
                  </DetailField>
                )}

                <DetailField label='Retainer Received'>
                  <Typography variant='h6' fontWeight={700} color='success.main'>
                    ${Number(caseData.retainer_amount || 0).toFixed(2)}
                  </Typography>
                </DetailField>

                {Number(caseData.balance_amount) > 0 && (
                  <DetailField label='Balance Due'>
                    <Typography variant='h6' fontWeight={700} color='warning.main'>
                      ${Number(caseData.balance_amount || 0).toFixed(2)}
                    </Typography>
                  </DetailField>
                )}

                <DetailField label='Payment'>
                  <Chip
                    label={
                      caseData.invoice_status === 'paid'
                        ? 'Paid'
                        : caseData.payment_status || 'Unpaid'
                    }
                    color={
                      caseData.invoice_status === 'paid' || caseData.payment_status === 'Paid'
                        ? 'success'
                        : caseData.payment_status === 'Partial'
                          ? 'warning'
                          : 'default'
                    }
                    size='small'
                  />
                </DetailField>

                {(caseData.invoice_date || caseData.payment_date) && (
                  <>
                    {caseData.invoice_date && (
                      <DetailField label='Invoice Date'>
                        <Typography variant='body1'>
                          {new Date(caseData.invoice_date).toLocaleDateString()}
                        </Typography>
                      </DetailField>
                    )}
                    {caseData.payment_date && (
                      <DetailField label='Payment Date'>
                        <Typography variant='body1'>
                          {new Date(caseData.payment_date).toLocaleDateString()}
                        </Typography>
                      </DetailField>
                    )}
                  </>
                )}
              </Box>
            </SectionCard>
          )}

          <SectionCard
            title='People & Meeting'
            subtitle='Firm, expert assignment, and scheduled meeting'
            icon={<GroupsIcon fontSize='small' />}
            sx={{ mt: 3 }}
          >
            <Stack spacing={2.5} divider={<Divider flexItem />}>
              {(isAdmin || isExpertUser) && caseData.firm?.name && (
                <DetailField label='Law Firm'>
                  <Stack direction='row' alignItems='center' spacing={1}>
                    <BusinessIcon fontSize='small' color='action' />
                    <Typography variant='body1'>{caseData.firm.name}</Typography>
                  </Stack>
                </DetailField>
              )}

              <DetailField label='Assigned Expert'>
                {caseData.assigned_expert ? (
                  <Stack direction='row' alignItems='center' spacing={1}>
                    <PersonIcon fontSize='small' color='action' />
                    <Box>
                      <Typography variant='body1' fontWeight={500}>
                        {caseData.assigned_expert.name}
                      </Typography>
                      <Typography variant='body2' color='text.secondary'>
                        {caseData.assigned_expert.email}
                      </Typography>
                    </Box>
                  </Stack>
                ) : (
                  <Typography variant='body2' color='text.secondary'>
                    No expert assigned yet
                  </Typography>
                )}
              </DetailField>

              {caseData.bookedSlot && (
                <DetailField label='Booked Meeting'>
                  <Stack spacing={1}>
                    <Stack direction='row' alignItems='center' spacing={1}>
                      <EventAvailableIcon fontSize='small' color='success' />
                      <Typography variant='body1' fontWeight={500}>
                        {new Date(caseData.bookedSlot.available_date).toLocaleDateString('en-US', {
                          weekday: 'long',
                          month: 'long',
                          day: 'numeric',
                          year: 'numeric',
                        })}
                      </Typography>
                    </Stack>
                    <Typography variant='body2' color='text.secondary' sx={{ pl: 3.5 }}>
                      {caseData.bookedSlot.time_slot}
                      {' · '}
                      Booked by {caseData.bookedSlot.booked_by?.name || 'Unknown'}
                    </Typography>
                  </Stack>
                </DetailField>
              )}
            </Stack>
          </SectionCard>

          {/* Retainer Form Section */}
          {(isAdmin || isExpertUser) && caseData.retainer_form_file_path && (
            <SectionCard
              title='Retainer & Invoice'
              subtitle='Submitted retainer form and invoice files'
              icon={<ReceiptIcon fontSize='small' />}
              sx={{ mt: 3 }}
            >
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} flexWrap='wrap'>
                <Button
                  variant='outlined'
                  startIcon={
                    downloadingRetainerForm ? (
                      <CircularProgress size={20} />
                    ) : (
                      <AssignmentIcon />
                    )
                  }
                  onClick={handleDownloadRetainerForm}
                  disabled={downloadingRetainerForm}
                >
                  {downloadingRetainerForm ? 'Downloading...' : 'Download Retainer Form'}
                </Button>

                {caseData.invoice_file_path && (
                  <Button
                    variant='outlined'
                    startIcon={
                      downloadingInvoice ? (
                        <CircularProgress size={20} />
                      ) : (
                        <AssignmentIcon />
                      )
                    }
                    onClick={handleDownloadInvoice}
                    disabled={downloadingInvoice}
                  >
                    {downloadingInvoice ? 'Downloading...' : 'Download Invoice'}
                  </Button>
                )}

                {caseData.invoice_status === 'unpaid' &&
                  caseData.invoice_file_path &&
                  isAdmin && (
                    <Button
                      variant='contained'
                      color='success'
                      onClick={handleMarkInvoicePaid}
                      disabled={markingInvoicePaid}
                      startIcon={
                        markingInvoicePaid ? (
                          <CircularProgress size={20} />
                        ) : (
                          <CheckCircleIcon />
                        )
                      }
                    >
                      {markingInvoicePaid ? 'Marking...' : 'Mark as Paid'}
                    </Button>
                  )}
              </Stack>

              <Stack direction='row' alignItems='center' spacing={1} sx={{ mt: 2 }}>
                <Chip
                  label={caseData.invoice_status === 'paid' ? 'Invoice Paid' : 'Invoice Unpaid'}
                  color={caseData.invoice_status === 'paid' ? 'success' : 'warning'}
                  size='small'
                />
                {caseData.invoice_status === 'paid' && caseData.invoice_paid_by?.name && (
                  <Typography variant='body2' color='text.secondary'>
                    Marked paid by {caseData.invoice_paid_by.name}
                  </Typography>
                )}
              </Stack>
            </SectionCard>
          )}

          {(isAdmin || isExpertUser) && !caseData.retainer_form_file_path && (
            <SectionCard
              title='Retainer & Invoice'
              subtitle='Not yet submitted'
              icon={<ReceiptIcon fontSize='small' />}
              sx={{ mt: 3 }}
            >
              <Typography variant='body2' color='text.secondary' sx={{ mb: 1 }}>
                Retainer form and invoice will appear here after submission.
              </Typography>
              {isAdmin && caseData.status === 'Approved' && (
                <Alert severity='info'>
                  Use &quot;Submit Retainer Form&quot; in the header to upload documents.
                </Alert>
              )}
            </SectionCard>
          )}

          {/* Intake Form Section */}
          {isAdmin && intakeFormLinks.length > 0 && (
            <Card sx={{ ...sectionCardSx, mt: 3 }}>
              <CardHeader
                title='Intake Forms'
                avatar={<AssignmentIcon color='primary' />}
              />
              <CardContent>
                {loadingIntakeFormLinks ? (
                  <Box
                    sx={{ display: 'flex', justifyContent: 'center', py: 2 }}
                  >
                    <CircularProgress size={24} />
                  </Box>
                ) : (
                  <Box>
                    {intakeFormLinks.map((link, index) => (
                      <Box
                        key={index}
                        sx={{
                          mb: 3,
                          p: 2,
                          border: '1px solid #e0e0e0',
                          borderRadius: 1,
                        }}
                      >
                        <Box
                          sx={{ display: 'flex', alignItems: 'center', mb: 2 }}
                        >
                          <AssignmentIcon
                            sx={{ mr: 1, color: 'primary.main' }}
                          />
                          <Typography variant='subtitle1' sx={{ flexGrow: 1 }}>
                            {link.intake_form_type.name}
                          </Typography>
                          <Chip
                            label={
                              link.status === 'completed'
                                ? 'Completed'
                                : 'Draft'
                            }
                            color={
                              link.status === 'completed'
                                ? 'success'
                                : 'warning'
                            }
                            size='small'
                            onClick={() => false}
                          />
                        </Box>

                        <Box
                          sx={{
                            display: 'flex',
                            gap: 1,
                            mb: 2,
                            flexWrap: 'wrap',
                          }}
                        >
                          <Button
                            variant='outlined'
                            startIcon={<LinkIcon />}
                            href={link.public_url}
                            target='_blank'
                            rel='noopener noreferrer'
                            size='small'
                          >
                            View Form
                          </Button>

                          {link.status === 'completed' && (
                            <>
                              <Button
                                variant='outlined'
                                startIcon={
                                  downloadingIntakeFormPdf ? (
                                    <CircularProgress size={16} />
                                  ) : (
                                    <AssignmentIcon />
                                  )
                                }
                                onClick={handleDownloadIntakeFormPdf}
                                disabled={downloadingIntakeFormPdf}
                                size='small'
                              >
                                {downloadingIntakeFormPdf
                                  ? 'Downloading...'
                                  : 'Download PDF'}
                              </Button>

                              {/* <Button
                                variant='outlined'
                                startIcon={
                                  generatingIntakeFormPdf ? (
                                    <CircularProgress size={16} />
                                  ) : (
                                    <AssignmentIcon />
                                  )
                                }
                                onClick={handleGenerateIntakeFormPdf}
                                disabled={generatingIntakeFormPdf}
                                size='small'
                              >
                                {generatingIntakeFormPdf ? 'Generating...' : 'Generate PDF'}
                              </Button> */}
                            </>
                          )}

                          {link.status === 'draft' && (
                            <Alert
                              severity='info'
                              sx={{ mt: 1, width: '100%' }}
                            >
                              PDF will be available once the client completes
                              this form.
                            </Alert>
                          )}
                        </Box>

                        <Typography variant='body2' color='text.secondary'>
                          {link.is_required ? 'Required form' : 'Optional form'}{' '}
                          -{' '}
                          {link.intake_form_type.description ||
                            'No description available'}
                        </Typography>
                      </Box>
                    ))}

                    <Alert severity='info' sx={{ mt: 2 }}>
                      <Typography variant='body2' sx={{ mb: 1 }}>
                        <strong>Workflow:</strong>
                      </Typography>
                      <Typography
                        variant='body2'
                        component='div'
                        sx={{ mb: 0.5 }}
                      >
                        1. Share the form links with the client to complete the
                        intake forms
                      </Typography>
                      <Typography
                        variant='body2'
                        component='div'
                        sx={{ mb: 0.5 }}
                      >
                        2. Once the client submits a form, it will be marked as
                        &quot;Completed&quot;
                      </Typography>
                      <Typography variant='body2' component='div'>
                        3. You can then download or generate PDF documents from
                        the completed forms
                      </Typography>
                    </Alert>
                  </Box>
                )}
              </CardContent>
            </Card>
          )}

          {/* Teams Meeting Section */}
          {isAdmin && caseData?.status === 'Meeting_Scheduled' && (
            <Card sx={{ ...sectionCardSx, mt: 3 }}>
              <CardHeader
                title='Teams Meeting'
                action={
                  <Button
                    startIcon={<VideoCallIcon />}
                    onClick={() => setShowTeamsMeetingDialog(true)}
                    variant='contained'
                    color='primary'
                    size='small'
                  >
                    Create Teams Meeting
                  </Button>
                }
              />
              <CardContent>
                {caseData.meeting_link ? (
                  <Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <VideoCallIcon sx={{ mr: 1, color: 'success.main' }} />
                      <Typography variant='subtitle1' color='success.main'>
                        Teams Meeting Created
                      </Typography>
                    </Box>
                    <Button
                      variant='outlined'
                      startIcon={<LinkIcon />}
                      href={caseData.meeting_link}
                      target='_blank'
                      rel='noopener noreferrer'
                      sx={{ mb: 2 }}
                    >
                      Join Teams Meeting
                    </Button>
                    <Typography variant='body2' color='text.secondary'>
                      Meeting link has been sent to the client.
                    </Typography>
                  </Box>
                ) : (
                  <Box>
                    <Typography
                      variant='body2'
                      color='text.secondary'
                      sx={{ mb: 2 }}
                    >
                      No Teams meeting has been created yet. Click the button
                      above to create a Teams meeting for this case.
                    </Typography>
                    <Alert severity='info'>
                      Creating a Teams meeting will send a confirmation email to
                      the case creator with the meeting link.
                    </Alert>
                  </Box>
                )}
              </CardContent>
            </Card>
          )}

          {/* Meeting Slots — hide once a slot is booked (details are in People & Meeting) */}
          {!caseData.bookedSlot &&
            (isAdmin ||
              (isExpertUser &&
                caseData?.assigned_expert?.id === user?.id)) && (
            <SectionCard
              title='Meeting Slots'
              subtitle='Create and send available times to the firm'
              icon={<EventIcon fontSize='small' />}
              sx={{ mt: 3 }}
              action={
                <Button
                  startIcon={<EventIcon />}
                  onClick={() => navigate(`/admin/cases/${id}/slots`)}
                  variant='contained'
                  size='small'
                >
                  Manage Slots
                </Button>
              }
            >
              <Typography variant='body2' color='text.secondary'>
                Fetch calendar availability, create bookable slots, and send selection
                links to the firm admin.
              </Typography>
            </SectionCard>
          )}
        </Box>

        {/* Case History — sticky scrollable sidebar */}
        <Box
          sx={{
            position: { lg: 'sticky' },
            top: 16,
            alignSelf: 'start',
          }}
        >
          <Card
            sx={{
              ...sectionCardSx,
              display: 'flex',
              flexDirection: 'column',
              maxHeight: { xs: 480, lg: 'calc(100vh - 120px)' },
              overflow: 'hidden',
            }}
          >
            <Box
              sx={{
                px: 2.5,
                py: 1.75,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 2,
                borderBottom: '1px solid',
                borderColor: 'divider',
                bgcolor: theme => theme.palette.mode === 'light' ? 'grey.50' : 'action.hover',
                flexShrink: 0,
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Avatar
                  variant='rounded'
                  sx={{ width: 38, height: 38, bgcolor: 'primary.main' }}
                >
                  <HistoryIcon fontSize='small' />
                </Avatar>
                <Box>
                  <Typography variant='subtitle1' fontWeight={700}>
                    Case History
                  </Typography>
                  <Typography variant='caption' color='text.secondary'>
                    {caseData.notes?.length
                      ? `${caseData.notes.length} entr${caseData.notes.length === 1 ? 'y' : 'ies'}`
                      : 'Activity timeline'}
                  </Typography>
                </Box>
              </Box>
              {(isAdmin || isFirmAdmin || isExpertUser) && (
                <Button
                  startIcon={<AddIcon />}
                  onClick={() => setShowAddNote(true)}
                  variant='outlined'
                  size='small'
                >
                  Add
                </Button>
              )}
            </Box>
            <Box
              sx={{
                overflowY: 'auto',
                flex: 1,
                px: 1,
                py: 1,
                '&::-webkit-scrollbar': { width: 6 },
                '&::-webkit-scrollbar-thumb': {
                  bgcolor: 'action.disabled',
                  borderRadius: 3,
                },
              }}
            >
              {caseData.notes && caseData.notes.length > 0 ? (
                <List disablePadding>
                  {caseData.notes.map((note, index) => (
                    <React.Fragment key={note.id}>
                      <ListItem alignItems='flex-start' sx={{ px: 1.5, py: 1.5 }}>
                        <ListItemAvatar sx={{ minWidth: 44 }}>
                          <Avatar sx={{ width: 34, height: 34, fontSize: '0.85rem' }}>
                            {note.user.name.charAt(0).toUpperCase()}
                          </Avatar>
                        </ListItemAvatar>
                        <ListItemText
                          disableTypography
                          primary={
                            <Box
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                flexWrap: 'wrap',
                                gap: 0.75,
                                mb: 0.75,
                              }}
                            >
                              <Typography variant='subtitle2' fontWeight={600}>
                                {note.user.name}
                              </Typography>
                              <Chip
                                label={note.action_type.replace(/_/g, ' ')}
                                size='small'
                                color={
                                  note.action_type === 'status_change'
                                    ? 'warning'
                                    : note.action_type === 'email_sent'
                                      ? 'info'
                                      : note.action_type === 'case_created'
                                        ? 'success'
                                        : 'default'
                                }
                                variant='outlined'
                                sx={{ height: 22, fontSize: '0.7rem' }}
                              />
                            </Box>
                          }
                          secondary={
                            <Box>
                              <Typography
                                variant='body2'
                                color='text.primary'
                                sx={{ mb: 0.75, lineHeight: 1.5 }}
                              >
                                {note.note}
                              </Typography>
                              <Typography variant='caption' color='text.secondary'>
                                {formatDate(note.created_at)}
                              </Typography>
                            </Box>
                          }
                        />
                      </ListItem>
                      {index < caseData.notes!.length - 1 && (
                        <Divider component='li' variant='inset' />
                      )}
                    </React.Fragment>
                  ))}
                </List>
              ) : (
                <Box sx={{ py: 6, px: 2, textAlign: 'center' }}>
                  <HistoryIcon sx={{ fontSize: 40, color: 'action.disabled', mb: 1 }} />
                  <Typography variant='body2' color='text.secondary'>
                    No activity yet. Add a note to start tracking case history.
                  </Typography>
                </Box>
              )}
            </Box>
          </Card>
        </Box>
      </Box>

      {/* Attachments Section */}
      <Box sx={{ mt: 3 }}>
        <SectionCard
          title='Case Attachments'
          subtitle='Service documents, expert resumes, and medical records'
          icon={<FolderOpenIcon fontSize='small' />}
          action={
            <Button
              variant='outlined'
              size='small'
              startIcon={<LinkIcon />}
              onClick={() => {
                navigator.clipboard.writeText(
                  `${window.location.origin}/medical-records/upload/${caseData.id}`
                );
                toast.success('Medical records upload link copied');
              }}
            >
              Copy Upload Link
            </Button>
          }
        >
          {(caseData.attachments ?? []).length === 0 ? (
            <Typography variant='body2' color='text.secondary' sx={{ py: 2, px: 0.5 }}>
              No attachments for this case yet.
            </Typography>
          ) : (
            <TableContainer>
              <Table size='small'>
                <TableHead>
                  <TableRow>
                    <TableCell>Name</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell>Added</TableCell>
                    <TableCell align='right'>Action</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(caseData.attachments ?? []).map(attachment => {
                    const attachmentKey = `${attachment.type}_${attachment.id}`;
                    return (
                      <TableRow key={attachmentKey} hover>
                        <TableCell>{attachment.name}</TableCell>
                        <TableCell>{ATTACHMENT_TYPE_LABEL[attachment.type]}</TableCell>
                        <TableCell>{formatDate(attachment.created_at)}</TableCell>
                        <TableCell align='right'>
                          <Button
                            variant='outlined'
                            size='small'
                            onClick={() => {
                              if (attachment.external_url) {
                                window.open(attachment.external_url, '_blank', 'noopener,noreferrer');
                                return;
                              }
                              handleDownloadAttachment(attachment);
                            }}
                            disabled={!attachment.external_url && downloadingAttachments[attachmentKey]}
                          >
                            {attachment.external_url
                              ? 'Open link'
                              : downloadingAttachments[attachmentKey]
                                ? 'Downloading...'
                                : 'Download'}
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </SectionCard>
      </Box>

      {/* Add Note Dialog */}
      <Dialog
        open={showAddNote}
        onClose={() => setShowAddNote(false)}
        maxWidth='sm'
        fullWidth
      >
        <DialogTitle>Add Note</DialogTitle>
        <DialogContent>
          <FormControl fullWidth sx={{ mb: 2, mt: 1 }}>
            <InputLabel>Note Type</InputLabel>
            <Select
              value={noteType}
              label='Note Type'
              onChange={(e: SelectChangeEvent) => setNoteType(e.target.value)}
            >
              <MenuItem value='comment'>Comment</MenuItem>
              {isAdmin && (
                <MenuItem value='status_change'>Status Change</MenuItem>
              )}
              <MenuItem value='other'>Other</MenuItem>
            </Select>
          </FormControl>
          <TextField
            autoFocus
            margin='dense'
            label='Note'
            type='text'
            fullWidth
            variant='outlined'
            multiline
            rows={4}
            value={newNote}
            onChange={e => setNewNote(e.target.value)}
            disabled={submitting}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowAddNote(false)} disabled={submitting}>
            Cancel
          </Button>
          <Button
            onClick={handleAddNote}
            disabled={!newNote.trim() || submitting}
            variant='contained'
          >
            {submitting ? 'Adding...' : 'Add Note'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Update Status Dialog */}
      <Dialog
        open={showStatusDialog}
        onClose={() => setShowStatusDialog(false)}
        maxWidth='sm'
        fullWidth
      >
        <DialogTitle>Update Case Status</DialogTitle>
        <DialogContent>
          <FormControl fullWidth sx={{ mb: 2, mt: 1 }}>
            <InputLabel>New Status</InputLabel>
            <Select
              value={newStatus}
              label='New Status'
              onChange={(e: SelectChangeEvent) => setNewStatus(e.target.value)}
            >
              <MenuItem value='Created'>Created</MenuItem>
              <MenuItem value='Prospecting'>Prospecting</MenuItem>
              <MenuItem value='Approved'>Approved</MenuItem>
              <MenuItem value='Retainer_Received'>Retainer Received</MenuItem>
              <MenuItem value='In_Person_Requested'>
                In-Person Requested
              </MenuItem>
              <MenuItem value='Virtual_Meeting_Requested'>
                Virtual Meeting Requested
              </MenuItem>
              <MenuItem value='Meeting_Scheduled'>Meeting Scheduled</MenuItem>
              <MenuItem value='Evaluation_Completed'>
                Evaluation Completed
              </MenuItem>
              <MenuItem value='Rejected'>Rejected</MenuItem>
            </Select>
          </FormControl>
          <TextField
            margin='dense'
            label='Status Note (Optional)'
            type='text'
            fullWidth
            variant='outlined'
            multiline
            rows={3}
            value={statusNote}
            onChange={e => setStatusNote(e.target.value)}
            disabled={submitting}
            placeholder='Add a note about this status change...'
          />
          {newStatus === 'Rejected' && (
            <Typography variant='body2' color='warning.main' sx={{ mt: 1 }}>
              Note: Rejection will send an email to the client with your note as the rejection reason.
            </Typography>
          )}
          {newStatus === 'Approved' && (
            <Typography variant='body2' color='success.main' sx={{ mt: 1 }}>
              Note: Approval will send an email to the client with all relevant attachments.
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setShowStatusDialog(false)}
            disabled={submitting}
          >
            Cancel
          </Button>
          <Button
            onClick={handleUpdateStatus}
            disabled={!newStatus || submitting}
            variant='contained'
            color={
              newStatus === 'Rejected'
                ? 'error'
                : newStatus === 'Approved'
                  ? 'success'
                  : 'primary'
            }
          >
            {submitting ? 'Updating...' : `Update to ${newStatus}`}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Teams Meeting Dialog */}
      <Dialog
        open={showTeamsMeetingDialog}
        onClose={() => setShowTeamsMeetingDialog(false)}
        maxWidth='sm'
        fullWidth
      >
        <DialogTitle>Create Teams Meeting</DialogTitle>
        <DialogContent>
          <Typography variant='body2' color='text.secondary' sx={{ mb: 2 }}>
            Create a Microsoft Teams meeting for this case. The meeting link
            will be sent to the case creator.
          </Typography>
          <TextField
            autoFocus
            margin='dense'
            label='Meeting Subject'
            type='text'
            fullWidth
            variant='outlined'
            value={teamsMeetingSubject}
            onChange={e => setTeamsMeetingSubject(e.target.value)}
            disabled={creatingTeamsMeeting}
            placeholder='Vocational Evaluation - [Case Name]'
            sx={{ mb: 2 }}
          />
          <TextField
            margin='dense'
            label='Meeting Description'
            type='text'
            fullWidth
            variant='outlined'
            multiline
            rows={4}
            value={teamsMeetingBody}
            onChange={e => setTeamsMeetingBody(e.target.value)}
            disabled={creatingTeamsMeeting}
            placeholder='Meeting details and instructions...'
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setShowTeamsMeetingDialog(false)}
            disabled={creatingTeamsMeeting}
          >
            Cancel
          </Button>
          <Button
            onClick={handleCreateTeamsMeeting}
            disabled={creatingTeamsMeeting}
            variant='contained'
            color='primary'
          >
            {creatingTeamsMeeting ? 'Creating...' : 'Create Teams Meeting'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Retainer Form Dialog */}
      <Dialog
        open={showRetainerFormDialog}
        onClose={() => setShowRetainerFormDialog(false)}
        maxWidth='md'
        fullWidth
      >
        <DialogTitle>Submit Retainer Form & Mark as Received</DialogTitle>
        <DialogContent>
          <Typography variant='body2' color='text.secondary' sx={{ mb: 2 }}>
            Please complete the retainer form below. Once submitted
            successfully, the case will be marked as &quot;Retainer
            Received&quot;.
          </Typography>
          <RetainerForm
            caseId={Number(id)}
            caseName={caseData?.case_name || ''}
            isRush={caseData?.is_rush || false}
            onSuccess={handleRetainerFormSuccess}
            onCancel={() => setShowRetainerFormDialog(false)}
          />
        </DialogContent>
      </Dialog>

      {/* Update Retainer Amount Dialog */}
      <Dialog
        open={showRetainerDialog}
        onClose={() => setShowRetainerDialog(false)}
        maxWidth='sm'
        fullWidth
      >
        <DialogTitle>Update Retainer Amount</DialogTitle>
        <DialogContent>
          <Typography variant='body2' color='text.secondary' sx={{ mb: 2 }}>
            Update the retainer amount received for this case. The balance will be automatically calculated.
          </Typography>
          <TextField
            autoFocus
            margin='dense'
            label='Retainer Amount'
            type='number'
            fullWidth
            variant='outlined'
            value={retainerAmount}
            onChange={e => setRetainerAmount(e.target.value)}
            disabled={updatingRetainer}
            placeholder='0.00'
            inputProps={{ min: 0, max: Number(caseData?.total_amount || 0), step: 0.01 }}
            helperText={`Maximum: $${Number(caseData?.total_amount || 0).toFixed(2)}`}
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setShowRetainerDialog(false)}
            disabled={updatingRetainer}
          >
            Cancel
          </Button>
          <Button
            onClick={handleUpdateRetainer}
            disabled={!retainerAmount || updatingRetainer}
            variant='contained'
            color='primary'
          >
            {updatingRetainer ? 'Updating...' : 'Update Retainer'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Update Invoice Details Dialog */}
      <Dialog
        open={showInvoiceDialog}
        onClose={() => setShowInvoiceDialog(false)}
        maxWidth='sm'
        fullWidth
      >
        <DialogTitle>Update Invoice Details</DialogTitle>
        <DialogContent>
          <Typography variant='body2' color='text.secondary' sx={{ mb: 2 }}>
            Update the invoice and payment dates for this case.
          </Typography>
          <TextField
            autoFocus
            margin='dense'
            label='Invoice Date'
            type='date'
            fullWidth
            variant='outlined'
            value={invoiceDate}
            onChange={e => setInvoiceDate(e.target.value)}
            disabled={updatingInvoice}
            InputLabelProps={{ shrink: true }}
            sx={{ mb: 2 }}
          />
          <TextField
            margin='dense'
            label='Payment Date'
            type='date'
            fullWidth
            variant='outlined'
            value={paymentDate}
            onChange={e => setPaymentDate(e.target.value)}
            disabled={updatingInvoice}
            InputLabelProps={{ shrink: true }}
            helperText='Leave empty if payment has not been received'
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setShowInvoiceDialog(false)}
            disabled={updatingInvoice}
          >
            Cancel
          </Button>
          <Button
            onClick={handleUpdateInvoiceDetails}
            disabled={updatingInvoice}
            variant='contained'
            color='primary'
          >
            {updatingInvoice ? 'Updating...' : 'Update Invoice Details'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Apply Rush Tier Dialog (admin only) ───────────────────────── */}
      <Dialog open={showRushTierDialog} onClose={() => setShowRushTierDialog(false)} maxWidth='sm' fullWidth>
        <DialogTitle>Apply Rush Tier</DialogTitle>
        <DialogContent>
          <Typography variant='body2' color='text.secondary' sx={{ mb: 2 }}>
            Select a rush tier to apply an additional fee to this case. Leave empty to remove the current rush tier.
          </Typography>

          {/* Current tier indicator */}
          {caseData?.rush_tier && (
            <Typography variant='body2' sx={{ mb: 2 }}>
              Current tier:{' '}
              <strong>
                {caseData.service_type?.rush_tiers?.find(t => t.key === caseData.rush_tier)?.label ?? caseData.rush_tier}
              </strong>{' '}
              — Rush fee: <strong>${Number(caseData.rush_fee_amount || 0).toFixed(2)}</strong>
            </Typography>
          )}

          {/* Tier options */}
          {caseData?.service_type?.rush_tiers && caseData.service_type.rush_tiers.length > 0 ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {/* No rush option */}
              <Box
                onClick={() => setSelectedRushTier('')}
                sx={{
                  p: 1.5,
                  border: '2px solid',
                  borderColor: selectedRushTier === '' ? 'primary.main' : 'divider',
                  borderRadius: 1,
                  cursor: 'pointer',
                  bgcolor: selectedRushTier === '' ? 'primary.50' : 'background.paper',
                  '&:hover': { borderColor: 'primary.light' },
                }}
              >
                <Typography variant='body2' fontWeight={600}>No Rush</Typography>
                <Typography variant='caption' color='text.secondary'>Remove rush tier — standard turnaround</Typography>
              </Box>

              {caseData.service_type.rush_tiers.map(tier => (
                <Box
                  key={tier.key}
                  onClick={() => setSelectedRushTier(tier.key)}
                  sx={{
                    p: 1.5,
                    border: '2px solid',
                    borderColor: selectedRushTier === tier.key ? 'error.main' : 'divider',
                    borderRadius: 1,
                    cursor: 'pointer',
                    bgcolor: selectedRushTier === tier.key ? 'error.50' : 'background.paper',
                    '&:hover': { borderColor: 'error.light' },
                  }}
                >
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant='body2' fontWeight={700} color='error.main'>{tier.label}</Typography>
                    <Typography variant='body2' fontWeight={700} color='error.main'>
                      +${Number(tier.fee).toLocaleString()}
                    </Typography>
                  </Box>
                  <Typography variant='caption' color='text.secondary'>
                    {tier.min_days}–{tier.max_days} business day turnaround
                  </Typography>
                </Box>
              ))}
            </Box>
          ) : (
            <Typography variant='body2' color='warning.main'>
              No rush tiers configured for this service type. Please configure tiers in the Service Type settings first.
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowRushTierDialog(false)} disabled={applyingRushTier}>
            Cancel
          </Button>
          <Button
            onClick={handleApplyRushTier}
            variant='contained'
            color={selectedRushTier ? 'error' : 'primary'}
            disabled={applyingRushTier || !caseData?.service_type?.rush_tiers?.length}
          >
            {applyingRushTier ? 'Applying…' : selectedRushTier ? 'Apply Rush Tier' : 'Remove Rush Tier'}
          </Button>
        </DialogActions>
      </Dialog>

    </Box>
  );
};

export default CaseSummary;
